import { Alert, CircularProgress } from '@mui/material';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usersApi } from 'src/api/user';
import { UploadImage } from 'src/components/common/UploadImage/UploadImage';
import { MaterialCircularProgress } from 'src/components/material/CircularProgress';
import { PathRoutes } from 'src/const/Routes/PathRoutes';
import { useAppDispatch } from 'src/hooks/Redux/useAppDispatch';
import { fetchLoggedUser } from 'src/redux/slices/loggedUserSlice';
import { CommonField } from './CommonField/CommonField';
import styles from './EditProfile.module.scss';
import { Header } from './Header/Header';
import { BIO_FIELD, EMAIL_FIELD, NAME_FIELD } from './const';

// TODO merge create and edit profile into a single reusable page
interface ProfileFormData {
  _id: string;
  full_name: string;
  avatar_id: string;
  avatar_url: string;
  bio: string;
  email: string;
}

type FormErrors = Partial<
  Omit<ProfileFormData & { general?: string }, '_id' | 'avatar_url'>
>;

export const EditProfile = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [formData, setFormData] = useState<ProfileFormData>({
    _id: '',
    full_name: '',
    avatar_id: '',
    avatar_url: '',
    bio: '',
    email: '',
  });

  const [loading, setLoading] = useState<boolean>(true);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<FormErrors | null>(null);

  useEffect(() => {
    setLoading(true);

    usersApi
      .userMe()
      .then((response) => {
        setFormData({
          _id: response.result._id,
          full_name: response.result.full_name ?? '',
          avatar_id: response.result.avatar_id ?? '',
          avatar_url: response.result.avatar_url ?? '',
          bio: response.result.bio ?? '',
          email: response.result.email ?? '',
        });
      })
      .catch((error) => {
        console.error('Error occurred trying to fetch user information', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitted(true);

    if (!validateForm()) {
      return;
    }

    setSaving(true);

    const updatedProfile = {
      full_name: formData.full_name,
      avatar_id: formData.avatar_id,
      bio: formData.bio,
      email: formData.email,
    };

    usersApi
      .updateById(formData._id, updatedProfile)
      .then(() => {
        dispatch(fetchLoggedUser());
        navigate(PathRoutes.ProfileRoute);
      })
      .catch((error) => {
        console.error('Error occurred while updating your profile', error);
      })
      .finally(() => setSaving(false));
  };

  const validateForm = (): boolean => {
    if (!formData.full_name) {
      setError({
        ...(!formData.full_name && { full_name: 'Required' }),
      });
      return false;
    }

    setError(null);
    return true;
  };

  const handleChange = (fieldName: string, value: string) => {
    setFormData({ ...formData, [fieldName]: value });
    setError((prev) => ({
      ...prev,
      [fieldName]: value ? undefined : 'Required',
    }));
  };

  if (loading) {
    return <MaterialCircularProgress />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.wrapper}>
        <Header />

        <form onSubmit={handleSubmit} noValidate>
          {error?.general && <Alert severity="error">{error.general}</Alert>}

          <span>
            <CommonField
              title={NAME_FIELD.title}
              description={NAME_FIELD.description}
              placeholder={NAME_FIELD.placeholder}
              charactersLength={NAME_FIELD.charactersLength}
              isIcon
              anchor="info-name"
              value={formData.full_name}
              error={submitted ? error?.full_name : undefined}
              onChange={(event) =>
                handleChange('full_name', event.target.value)
              }
            />
            <UploadImage
              defaultImage={
                typeof formData.avatar_url === 'string'
                  ? formData.avatar_url
                  : ''
              }
              error={submitted ? error?.avatar_id : undefined}
              onUpdate={(fileId: string) => handleChange('avatar_id', fileId)}
            >
              Add Photo
            </UploadImage>
            <CommonField
              type="textarea"
              title={BIO_FIELD.title}
              description={BIO_FIELD.description}
              placeholder={BIO_FIELD.placeholder}
              charactersLength={BIO_FIELD.charactersLength}
              anchor="info-bio"
              isIcon
              value={formData.bio}
              error={submitted ? error?.bio : undefined}
              onChange={(event) => handleChange('bio', event.target.value)}
            />
            <CommonField
              title={EMAIL_FIELD.title}
              description={EMAIL_FIELD.description}
              placeholder={EMAIL_FIELD.placeholder}
              charactersLength={EMAIL_FIELD.charactersLength}
              isIcon
              anchor="info-email"
              value={formData.email}
              error={submitted ? error?.email : undefined}
              onChange={(event) => handleChange('email', event.target.value)}
            />
          </span>

          <button
            type="submit"
            className={clsx(styles.button, styles['button--primary'])}
            disabled={saving}
          >
            {saving ? (
              <CircularProgress
                sx={{ color: '#fff' }}
                size={28}
                thickness={4}
              />
            ) : (
              'Save Profile'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};
